import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MoveRight } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { IMAGES } from "@/lib/images";
import { ROUTES } from "@/lib/fleet";
import { Reveal, SectionHead } from "./Reveal";

export const Journeys = () => {
  const { t } = useI18n();
  return (
    <section className="relative border-y border-white/5 overflow-hidden">
      <div className="absolute inset-0">
        <img src={IMAGES.riviera} alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C0E] via-[#0B0C0E]/85 to-[#0B0C0E]" />
      </div>
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10 py-28 lg:py-36">
        <SectionHead eyebrow={t("journeys.eyebrow")} title={t("journeys.title")} />
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/8 border border-white/8">
          {ROUTES.map(([a, b], i) => (
            <Reveal key={`${a}-${b}`} delay={i * 0.05}>
              <Link to="/request" data-testid={`journey-route-${i}`}
                className="group flex items-center justify-between bg-[#0B0C0E]/80 px-6 py-6 hover:bg-[#121418] transition-colors duration-400">
                <span className="font-serif-elt text-lg font-light text-[var(--elt-ivory)]">{a}</span>
                <MoveRight size={16} className="text-[var(--elt-gold)] group-hover:translate-x-1 transition-transform duration-400" />
                <span className="font-serif-elt text-lg font-light text-[var(--elt-ivory)]">{b}</span>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-wrap gap-2">
            {t("journeys.offers").map((s) => (
              <span key={s} className="text-[0.65rem] tracking-[0.2em] uppercase text-[var(--elt-muted)] border border-white/10 px-4 py-2">{s}</span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const Pillars = () => {
  const { t } = useI18n();
  return (
    <section className="max-w-[1440px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
      <SectionHead eyebrow={t("pillars.eyebrow")} title={t("pillars.title")} className="text-center [&>div]:justify-center [&_h2]:mx-auto" />
      <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-12">
        {t("pillars.items").map((p, i) => (
          <motion.div key={p} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: i * 0.09, ease: [0.22, 1, 0.36, 1] }}
            className="group py-10 border-b border-white/8 flex items-baseline gap-6" data-testid={`pillar-${i}`}>
            <span className="text-[0.62rem] tracking-[0.3em] text-[var(--elt-gold)]/60">0{i + 1}</span>
            <span className="font-serif-elt text-3xl lg:text-4xl font-light text-[var(--elt-ivory)]/80 group-hover:text-[var(--elt-gold-light)] transition-colors duration-500">{p}</span>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export const About = () => {
  const { t } = useI18n();
  return (
    <section id="about" className="bg-[#0D0E11] border-y border-white/5">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-28 lg:py-36 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-6">
          <SectionHead eyebrow={t("about.eyebrow")} title={t("about.title")} />
          <Reveal delay={0.12}>
            <p className="mt-8 font-serif-elt italic text-xl text-[var(--elt-gold-light)] max-w-md leading-relaxed">{t("about.body1")}</p>
            <p className="mt-6 text-sm font-light text-[var(--elt-muted)] max-w-md leading-relaxed">{t("about.body2")}</p>
            <p className="mt-4 text-sm font-light text-[var(--elt-muted)] max-w-md leading-relaxed">{t("about.body3")}</p>
          </Reveal>
        </div>
        <div className="lg:col-span-4 lg:col-start-8 flex flex-col justify-center">
          {t("about.feel").map((f, i) => (
            <Reveal key={f} delay={0.1 + i * 0.08}>
              <p className="font-serif-elt text-3xl lg:text-4xl font-light text-[var(--elt-ivory)]/25 hover:text-[var(--elt-ivory)] transition-colors duration-700 leading-snug cursor-default">{f}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
